import { useCallback, useEffect, useRef, useState, type RefObject } from "react";

export interface UseStickToBottomResult {
  /** True while the view should follow new content (user is at the bottom). */
  isPinnedToBottom: boolean;
  /** Scroll the latest content into view (container-agnostic). */
  scrollToBottom: (behavior: ScrollBehavior) => void;
  /** Force the pin back on — e.g. after the user sends a message. */
  pinToBottom: () => void;
}

/**
 * Stick-to-bottom scroll behavior for a chat-style message list.
 *
 * The view follows new/streaming content ONLY while the user is parked at the
 * bottom. Once they scroll up to read, content stops yanking them down (a
 * "jump to latest" affordance, rendered by the caller, lets them return).
 *
 * Container-agnostic by design — it does not assume which element scrolls:
 *  - an `IntersectionObserver` on the bottom sentinel decides "at bottom?",
 *    ignoring the visibility flip caused by our own programmatic scroll;
 *  - a `wheel` / `touch` "scroll-up intent" listener releases the pin
 *    immediately (those events bubble, unlike `scroll`), which beats the
 *    streaming race where the next delta's auto-scroll would otherwise re-pin
 *    the user before the observer reports they left the bottom;
 *  - `scrollToBottom` uses `scrollIntoView`, which walks to the nearest scroll
 *    container regardless of breakpoint/layout.
 *
 * The caller owns the "scroll when new content arrives" effect (it knows the
 * content signals) and renders the jump control from `isPinnedToBottom`.
 */
export function useStickToBottom({
  scrollRef,
  sentinelRef,
  enabled,
}: {
  /** The element users wheel/touch over (the scroll region). */
  scrollRef: RefObject<HTMLElement | null>;
  /** A zero-height anchor at the very bottom of the message list. */
  sentinelRef: RefObject<HTMLElement | null>;
  /** Gate — typically `messages.length > 0`. */
  enabled: boolean;
}): UseStickToBottomResult {
  const [isPinnedToBottom, setIsPinnedToBottom] = useState(true);
  // Set while WE are programmatically scrolling, so the resulting visibility
  // change isn't mistaken for the user returning to the bottom.
  const isAutoScrollingRef = useRef(false);

  const scrollToBottom = useCallback(
    (behavior: ScrollBehavior) => {
      const node = sentinelRef.current;
      if (!node || typeof node.scrollIntoView !== "function") return;
      isAutoScrollingRef.current = true;
      node.scrollIntoView({ behavior, block: "end" });
      window.setTimeout(
        () => {
          isAutoScrollingRef.current = false;
        },
        behavior === "smooth" ? 450 : 80,
      );
    },
    [sentinelRef],
  );

  const pinToBottom = useCallback(() => setIsPinnedToBottom(true), []);

  // Pin detection — observe the bottom sentinel. Visible → at the bottom (pin
  // on); off-screen → scrolled up (pin off). Guarded for jsdom (no IO).
  useEffect(() => {
    if (!enabled) return;
    const sentinel = sentinelRef.current;
    if (!sentinel || typeof IntersectionObserver === "undefined") return;
    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[entries.length - 1];
        if (!entry || isAutoScrollingRef.current) return;
        setIsPinnedToBottom(entry.isIntersecting);
      },
      { rootMargin: "0px 0px 24px 0px", threshold: 0 },
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [enabled, sentinelRef]);

  // A deliberate upward gesture releases the pin immediately. wheel/touch
  // bubble, so this fires regardless of which element actually scrolls.
  useEffect(() => {
    if (!enabled) return;
    const region = scrollRef.current;
    if (!region) return;
    const onWheel = (event: WheelEvent) => {
      if (event.deltaY < 0) setIsPinnedToBottom(false);
    };
    let lastTouchY: number | null = null;
    const onTouchStart = (event: TouchEvent) => {
      lastTouchY = event.touches[0]?.clientY ?? null;
    };
    const onTouchMove = (event: TouchEvent) => {
      const y = event.touches[0]?.clientY ?? null;
      // Finger moving DOWN scrolls content UP — release the pin.
      if (y !== null && lastTouchY !== null && y > lastTouchY + 2) {
        setIsPinnedToBottom(false);
      }
      lastTouchY = y;
    };
    region.addEventListener("wheel", onWheel, { passive: true });
    region.addEventListener("touchstart", onTouchStart, { passive: true });
    region.addEventListener("touchmove", onTouchMove, { passive: true });
    return () => {
      region.removeEventListener("wheel", onWheel);
      region.removeEventListener("touchstart", onTouchStart);
      region.removeEventListener("touchmove", onTouchMove);
    };
  }, [enabled, scrollRef]);

  return { isPinnedToBottom, scrollToBottom, pinToBottom };
}
